/**
 * Utilitaires pour extraire et formater les données des champs DisplayedFieldId
 * Utilisé pour afficher les informations détaillées d'un objet touristique
 */

import {
  ObjetTouristique,
  ElementReference,
  MoyenCommunicationType,
  type Ouverture,
  type PeriodeOuverture,
  type FermetureExceptionnelle,
  type PeriodeTarif,
  type Capacites,
  type OpenSystemWidget,
  type MetadonneeBloc,
  type MetadonneeContenu,
} from "../types/apidae.d";
import {
  WidgetLanguage,
  DisplayedFieldId,
  WidgetFieldsInfos,
} from "../types/widgets.d";
import {
  getMoyenCommunicationConfig,
  getMoyenCommunicationConfigById,
} from "../config/moyenCommunicationIcons";
import {
  getCoordonneesValue,
  getReservationSiteWebUrl,
} from "./itemFieldUtils";
import { filterRelevantOpeningPeriodes } from "./openingUtils";
import { getIllustrationImageUrl } from "./widgetsUtils";

/**
 * Paramètres nécessaires pour les fonctions utilitaires
 */
export interface DisplayedFieldUtilsParams {
  language: WidgetLanguage;
  getLabel: (libelle: any) => string;
  formatDate: (dateStr: string, options?: Intl.DateTimeFormatOptions) => string;
  t: (key: string) => string;
  fieldConfig?: WidgetFieldsInfos; // Configuration du champ (pour disponibilitesElloha, disponibilitesOpensystem)
}

/**
 * Type de retour pour les données d'un champ affiché
 */
export interface DisplayedFieldData {
  value?: string;
  items?: any[]; // Pour les champs qui retournent des listes (ex: equipements, multimedias)
  url?: string; // Pour les champs avec URL (ex: images, multimedias)
  // Champs optionnels supplémentaires pour certains DisplayedFieldId
  /** Badge Tourisme & Handicap (ACCESSIBILITE) : affiché si une distinction a un nom.enumKey parmi les 4 labels handicap */
  showTourismAndDisabilityBadge?: boolean;
  /** Descriptifs handicap (ACCESSIBILITE) : moteur, auditif, visuel, mental */
  descriptifs?: {
    moteur?: string;
    auditif?: string;
    visuel?: string;
    mental?: string;
  };
  rawValue?: string;
  animauxAcceptesSupplement?: any;
  descriptifAnimauxAcceptes?: string;
  /** URL du premier moyen de type site web (pour bouton Réserver) */
  reservationUrl?: string;
  /** Données Ouverture (champ OUVERTURE) */
  openingMode?: "AUTOMATIQUE" | "MANUEL";
  openingPeriodes?: PeriodeOuverture[];
  openingPeriodeEnClair?: string;
  openingFermeturesExceptionnelles?: FermetureExceptionnelle[];
  /** Données Tarifs (champ TARIFS) */
  pricingMode?: "AUTOMATIQUE" | "MANUEL";
  pricingPeriodes?: PeriodeTarif[];
  pricingTarifsEnClair?: string;
  /** Lignes affichables pour Itinéraire (champ INFOS_ITINERAIRES) */
  itineraryRows?: Array<{
    icon: string;
    label: string;
    value: string;
    unit: string;
  }>;
  /** Précisions balisage (affichées en encart disclaimer primary10) */
  precisionsBalisage?: string;
  /** Données capacités (champ CAPACITES, schéma v002) */
  capacites?: Capacites;
  /** Variant actif : hebergementCollectif | hebergementLocatif | hotelleriePleinAir | hotellerie */
  capacitesVariant?:
    | "hebergementCollectif"
    | "hebergementLocatif"
    | "hotelleriePleinAir"
    | "hotellerie";
  /** Données Widget Direct OpenSystem (DISPONIBILITES_OPENSYSTEM) */
  openSystemWidget?: {
    ui: string;
    integration: { id: number; idPanier: string };
    uis?: OpenSystemWidget["uis"];
  };
  /** Données Elloha (DISPONIBILITES_ELLOHA) : idProduct depuis metadonnees, idBookingEngine depuis config */
  ellohaWidget?: {
    idProduct: string;
    idBookingEngine: string;
  };
}

/** Type retourné par getOpenSystemWidgetFromItem */
export type OpenSystemWidgetData = NonNullable<
  DisplayedFieldData["openSystemWidget"]
>;

/** Données Elloha extraites de l'item (idProduct) ; idBookingEngine vient de la config. */
export type EllohaWidgetData = NonNullable<DisplayedFieldData["ellohaWidget"]>;

/**
 * Indique si le champ Elloha est configuré pour l'affichage (idBookingEngine dans la config).
 */
export function isEllohaConfigured(
  displayedFields: WidgetFieldsInfos[] | undefined,
): boolean {
  const field = displayedFields?.find(
    (f) => f.id === DisplayedFieldId.DISPONIBILITES_ELLOHA,
  );
  const data = field?.data as { idBookingEngine?: string } | undefined;
  return !!data?.idBookingEngine;
}

/**
 * Extrait le widget Elloha des métadonnées d'un objet (noeudId "elloha").
 * Utilisé pour DISPONIBILITES_ELLOHA (liste + détail) et picto ListItemCard.
 */
export function getEllohaWidgetFromItem(
  item: ObjetTouristique | null | undefined,
): EllohaWidgetData | undefined {
  const blocs = item?.metadonnees;
  if (!Array.isArray(blocs)) return undefined;
  const ellohaBlocs = blocs.filter((b: { noeudId?: string }) =>
    b.noeudId?.includes("elloha"),
  );
  for (const bloc of ellohaBlocs) {
    const contenus = (bloc as MetadonneeBloc).contenus;
    if (!Array.isArray(contenus)) continue;
    for (const c of contenus) {
      const meta = (c as MetadonneeContenu)?.metadonnee as
        | { id?: string | null }
        | undefined;
      if (meta?.id == null) continue;
      return { idProduct: meta.id, idBookingEngine: "" }; // idBookingEngine complété par la config dans getDisplayedFieldData
    }
  }
  return undefined;
}

/**
 * Indique si le champ OpenSystem est configuré pour l'affichage (idPanier + idIntegration dans la config).
 * À utiliser pour la section détail et le picto ListItemCard, pour rester cohérent avec getDisplayedFieldData.
 */
export function isOpenSystemConfigured(
  displayedFields: WidgetFieldsInfos[] | undefined,
): boolean {
  const field = displayedFields?.find(
    (f) => f.id === DisplayedFieldId.DISPONIBILITES_OPENSYSTEM,
  );
  const data = field?.data as
    | { idPanier?: string; idIntegration?: number }
    | undefined;
  return !!(data?.idPanier && data.idIntegration != null);
}

/**
 * Extrait le widget OpenSystem des métadonnées d'un objet (noeudId "open-system").
 * Utilisé pour DISPONIBILITES_OPENSYSTEM (liste + détail) et picto ListItemCard.
 */
export function getOpenSystemWidgetFromItem(
  item: ObjetTouristique | null | undefined,
): OpenSystemWidgetData | undefined {
  const blocs = item?.metadonnees;
  if (!Array.isArray(blocs)) return undefined;
  const openSystemBlocs = blocs.filter((b: { noeudId?: string }) =>
    b.noeudId?.includes("open-system"),
  );
  for (const bloc of openSystemBlocs) {
    const contenus = (bloc as MetadonneeBloc).contenus;
    if (!Array.isArray(contenus)) continue;
    for (const c of contenus) {
      const w = (c as MetadonneeContenu)?.metadonnee?.widget as
        | OpenSystemWidget
        | undefined;
      const ui = w?.uis?.[0]?.ui;
      if (ui == null || !w?.integration) continue;
      return {
        ui,
        integration: w.integration,
        uis: w.uis,
      };
    }
  }
  return undefined;
}

/**
 * Récupère les données d'un champ DisplayedFieldId pour un item
 */
export function getDisplayedFieldData(
  item: ObjetTouristique,
  fieldId: string,
  params: DisplayedFieldUtilsParams,
): DisplayedFieldData | null {
  switch (fieldId) {
    case DisplayedFieldId.DESCRIPTIF_COURT:
      const descriptifCourt = item.presentation?.descriptifCourt;
      if (!descriptifCourt) return null;
      return {
        value: params.getLabel(descriptifCourt),
      };

    case DisplayedFieldId.DESCRIPTIF_DETAILLE:
      const descriptifDetaille = item.presentation?.descriptifDetaille;
      if (!descriptifDetaille) return null;
      return {
        value: params.getLabel(descriptifDetaille),
      };

    case DisplayedFieldId.IMAGES: {
      const illustrations = item.illustrations || [];
      if (illustrations.length === 0) return null;
      const language = params.language as string;
      return {
        items: illustrations.map((ill) => {
          const url =
            getIllustrationImageUrl(ill as any, "original", language) || null;
          return {
            url,
            nom: params.getLabel(ill.nom),
            legende: params.getLabel(ill.legende),
          };
        }),
      };
    }

    case DisplayedFieldId.MULTIMEDIAS: {
      const multimedias = item.multimedias || [];
      if (multimedias.length === 0) return null;
      return {
        items: multimedias.map((media) => {
          const fichiers = media.traductionFichiers || [];
          const firstFile = fichiers[0];
          const url =
            fichiers.find((f) => f.locale === params.language)?.url ||
            firstFile?.url ||
            null;
          const labelFromNom = params.getLabel(media.nom);
          const fallbackName =
            firstFile?.fileName != null && firstFile?.extension != null
              ? `${firstFile.fileName}.${firstFile.extension}`
              : firstFile?.fileName ?? firstFile?.extension ?? "";
          return {
            url,
            nom: labelFromNom || fallbackName,
            legende: params.getLabel(media.legende),
            type: media.type,
            extension: firstFile?.extension?.toLowerCase() ?? "",
          };
        }),
      };
    }

    case DisplayedFieldId.MOYENS_COMMUNICATION: {
      const moyens = item.informations?.moyensCommunication || [];
      if (moyens.length === 0) return null;
      // URL pour le bouton Réserver : premier SITE_WEB dans reservation.organismes
      const reservationUrl = getReservationSiteWebUrl(item, params.language);
      const enumKeySiteWeb = MoyenCommunicationType.SITE_WEB;
      const items = moyens
        .map((moyen) => {
          const enumKey = moyen.type?.enumKey;
          const config =
            getMoyenCommunicationConfig(enumKey) ??
            getMoyenCommunicationConfigById(moyen.type?.id);
          if (!config) return null;
          const coordonnees = getCoordonneesValue(
            moyen.coordonnees,
            params.language,
          );
          const value = coordonnees || "";
          const isMail =
            enumKey === MoyenCommunicationType.MAIL || moyen.type?.id === 204;
          const isSiteWeb =
            enumKey === enumKeySiteWeb || moyen.type?.id === 205;
          const isPhone =
            enumKey === MoyenCommunicationType.TELEPHONE ||
            moyen.type?.id === 201;
          const linkUrl =
            isMail && value
              ? `mailto:${value}`
              : isPhone && value
                ? `tel:${value.replace(/\s/g, "")}`
                : isSiteWeb && value
                  ? value
                  : undefined;
          return {
            icon: config.icon,
            title: params.getLabel(moyen.type),
            content: value,
            url: linkUrl,
            enumKey,
            isSocial: config.isSocial,
          };
        })
        .filter(Boolean) as Array<{
        icon: string;
        title: string;
        content: string;
        url?: string;
        enumKey?: string;
        isSocial: boolean;
      }>;
      if (items.length === 0) return null;
      return {
        items,
        reservationUrl: reservationUrl ?? undefined,
      };
    }

    case DisplayedFieldId.OUVERTURE: {
      const ouverture: Ouverture | undefined = item.ouverture;
      if (!ouverture) return null;
      const allPeriodes = ouverture.periodesOuvertures ?? [];
      const periodes = filterRelevantOpeningPeriodes(allPeriodes);
      const mode = ouverture.periodeEnClairGenerationMode ?? "MANUEL";
      const periodeEnClairStr = ouverture.periodeEnClair
        ? params.getLabel(ouverture.periodeEnClair)
        : "";
      const hasPeriodes = periodes.length > 0;
      const hasManualText = !!periodeEnClairStr.trim();
      if (!hasPeriodes && !hasManualText) return null;
      return {
        openingMode: mode,
        openingPeriodes: periodes,
        openingPeriodeEnClair: periodeEnClairStr || undefined,
        openingFermeturesExceptionnelles:
          ouverture.fermeturesExceptionnelles ?? [],
      };
    }

    case DisplayedFieldId.TARIFS: {
      const descriptionTarif = (item as any).descriptionTarif;
      if (!descriptionTarif) return null;
      const tarifsEnClairStr = descriptionTarif.tarifsEnClair
        ? params.getLabel(descriptionTarif.tarifsEnClair)
        : null;
      const indicationTarif = descriptionTarif.indicationTarif || null;
      const gratuit = descriptionTarif.gratuit || false;
      const periodes = (descriptionTarif.periodes ?? []) as PeriodeTarif[];
      const mode = descriptionTarif.tarifsEnClairGenerationMode ?? "MANUEL";
      const hasPeriodes = periodes.length > 0;
      const hasManualText = !!(
        tarifsEnClairStr?.trim() ||
        indicationTarif ||
        (gratuit && (params.t("free") || "Gratuit"))
      );
      if (!hasPeriodes && !hasManualText) return null;
      return {
        value:
          tarifsEnClairStr ||
          indicationTarif ||
          (gratuit ? params.t("free") || "Gratuit" : ""),
        pricingMode: mode,
        pricingPeriodes: hasPeriodes ? periodes : undefined,
        pricingTarifsEnClair: hasManualText
          ? tarifsEnClairStr ||
            indicationTarif ||
            (gratuit ? params.t("free") || "Gratuit" : "")
          : undefined,
      };
    }

    case DisplayedFieldId.MOYEN_PAIEMENT: {
      const modesPaiement = (item as any).descriptionTarif?.modesPaiement || [];
      if (modesPaiement.length === 0) return null;
      return {
        items: modesPaiement.map((mode: ElementReference) => ({
          id: mode.id,
          enumKey: mode.enumKey ?? "",
          label: params.getLabel(mode),
        })),
      };
    }

    case DisplayedFieldId.INFORMATIONS_RESERVATION: {
      // Informations de réservation : reservation.complement (LibelleMultilingue)
      const reservation = (item as any).reservation;
      const complement = reservation?.complement;
      if (!complement) return null;
      const complementText = params.getLabel(complement);
      if (!complementText?.trim()) return null;
      return {
        value: complementText,
      };
    }

    case DisplayedFieldId.EQUIPEMENTS: {
      const prestationsEquip = (item as any).prestations || {};
      const infoActivite = (item as any).informationsActivite || {};
      const infoSejour = (item as any).informationsSejourPackage || {};
      const sources: Array<{ type: string; list: ElementReference[] }> = [
        { type: "equipement", list: prestationsEquip.equipements || [] },
        { type: "service", list: prestationsEquip.services || [] },
        { type: "confort", list: prestationsEquip.conforts || [] },
        { type: "activite", list: prestationsEquip.activites || [] },
        {
          type: "activiteCulturelle",
          list: infoActivite.activitesCulturelles || [],
        },
        {
          type: "activiteSportive",
          list: infoActivite.activitesSportives || [],
        },
        {
          type: "activiteCulturelle",
          list: infoSejour.activitesCulturelles || [],
        },
        { type: "activiteSportive", list: infoSejour.activitesSportives || [] },
      ];
      const items: Array<{
        type: string;
        id: number;
        enumKey?: string;
        label: string;
      }> = [];
      for (const { type, list } of sources) {
        for (const ref of list as ElementReference[]) {
          const id = ref?.id ?? 0;
          const enumKey = ref?.enumKey?.trim() || undefined;
          const label = params.getLabel(ref);
          if (label) items.push({ type, id, enumKey, label });
        }
      }
      if (items.length === 0) return null;
      return { items };
    }

    case DisplayedFieldId.LABEL_DISTINCTION: {
      // distinctions : nom (avec categorie sur le nom), qualification (facultatif)
      // Affichage : qualification si présente, sinon nom
      const distinctionsList = (item as any).distinctions || [];
      if (distinctionsList.length === 0) return null;
      return {
        items: distinctionsList.map((dist: any) => {
          const nom = dist.nom ? params.getLabel(dist.nom) : "";
          const qualification = dist.qualification
            ? params.getLabel(dist.qualification)
            : "";
          const categorie = dist.nom?.categorie
            ? params.getLabel(dist.nom.categorie)
            : null;
          const label = qualification?.trim() ? qualification : nom || "";
          return {
            nom: nom || "",
            qualification: qualification || "",
            categorie,
            label,
          };
        }),
      };
    }

    case DisplayedFieldId.ACCESSIBILITE: {
      const prestations = (item as any).prestations || {};
      const tourismesAdaptes = prestations.tourismesAdaptes || [];
      const distinctions = (item as any).distinctions || [];
      const tourismeHandicapEnumKeys = new Set([
        "PRESTATION_LABEL_TOURISME_HANDICAP_LABEL_AUDITIF",
        "PRESTATION_LABEL_TOURISME_HANDICAP_LABEL_MOTEUR",
        "PRESTATION_LABEL_TOURISME_HANDICAP_LABEL_VISUEL",
        "PRESTATION_LABEL_TOURISME_HANDICAP_LABEL_MENTAL",
      ]);
      const showTourismAndDisabilityBadge = distinctions.some(
        (d: any) =>
          d?.nom?.enumKey && tourismeHandicapEnumKeys.has(d.nom.enumKey),
      );
      const descriptifs = {
        moteur: prestations.descriptifHandicapMoteur
          ? params.getLabel(prestations.descriptifHandicapMoteur)
          : undefined,
        auditif: prestations.descriptifHandicapAuditif
          ? params.getLabel(prestations.descriptifHandicapAuditif)
          : undefined,
        visuel: prestations.descriptifHandicapVisuel
          ? params.getLabel(prestations.descriptifHandicapVisuel)
          : undefined,
        mental: prestations.descriptifHandicapMental
          ? params.getLabel(prestations.descriptifHandicapMental)
          : undefined,
      };
      if (
        tourismesAdaptes.length === 0 &&
        !showTourismAndDisabilityBadge &&
        !Object.values(descriptifs).some(Boolean)
      )
        return null;
      return {
        items: tourismesAdaptes.map((ta: ElementReference) => ({
          id: (ta as any).id ?? 0,
          enumKey: (ta as any).enumKey,
          label: params.getLabel(ta),
        })),
        showTourismAndDisabilityBadge,
        descriptifs,
      };
    }

    case DisplayedFieldId.INFOS_ITINERAIRES: {
      const itineraire = (item as any).informationsEquipement?.itineraire;
      const altitudeGeoloc = item.localisation?.geolocalisation?.altitude;
      const rows: Array<{
        icon: string;
        label: string;
        value: string;
        unit: string;
      }> = [];
      const t = (key: string) => params.t(key) ?? key;

      const push = (
        icon: string,
        label: string,
        value: number | string | undefined | null,
        unit: string,
      ) => {
        if (value === undefined || value === null || value === "") return;
        const str =
          typeof value === "number" ? String(value) : String(value).trim();
        if (!str) return;
        rows.push({ icon, label, value: str, unit });
      };

      if (itineraire) {
        push(
          "bold/map_&_location/routing_2",
          t("distance"),
          itineraire.distance,
          "km",
        );
        push(
          "bold/nature_travel/altitude",
          t("altitudeMaximum"),
          itineraire.altitudeMaximum,
          "m",
        );
        push(
          "bold/nature_travel/altitude",
          t("altitudeMinimum"),
          itineraire.altitudeMinimum,
          "m",
        );
      }
      push("bold/nature_travel/landscape", t("altitude"), altitudeGeoloc, "m");
      if (itineraire) {
        push(
          "bold/nature_travel/elevation",
          t("denivellationPositive"),
          itineraire.denivellationPositive,
          "m",
        );
        push(
          "bold/nature_travel/descent",
          t("denivellationNegative"),
          itineraire.denivellationNegative,
          "m",
        );
      }

      const precisionsBalisageText = itineraire?.precisionsBalisage
        ? params.getLabel(itineraire.precisionsBalisage)?.trim()
        : undefined;

      if (rows.length === 0 && !precisionsBalisageText) return null;
      return {
        itineraryRows: rows,
        ...(precisionsBalisageText && {
          precisionsBalisage: precisionsBalisageText,
        }),
      };
    }

    case DisplayedFieldId.CAPACITES: {
      const capacites = (item as ObjetTouristique).capacites;
      if (!capacites) return null;

      const hasHCO = !!capacites.hebergementCollectif;
      const hasHLO = !!capacites.hebergementLocatif;
      const hasHPA = !!capacites.hotelleriePleinAir;
      const hasHOT = !!capacites.hotellerie;

      const variant = hasHCO
        ? "hebergementCollectif"
        : hasHLO
          ? "hebergementLocatif"
          : hasHPA
            ? "hotelleriePleinAir"
            : hasHOT
              ? "hotellerie"
              : null;
      if (!variant) return null;

      return {
        capacites,
        capacitesVariant: variant,
      };
    }

    case DisplayedFieldId.LOCATION_SALLE: {
      const tourismeAffaires = (item as any).tourismeAffaires;
      if (!tourismeAffaires || !tourismeAffaires.tourismeAffairesEnabled)
        return null;
      return {
        items: [
          {
            nombreSallesReunionEquipees:
              tourismeAffaires.nombreSallesReunionEquipees,
            capaciteMaxAccueil: tourismeAffaires.capaciteMaxAccueil,
            nombreSallesModulables: tourismeAffaires.nombreSallesModulables,
          },
        ],
      };
    }

    case DisplayedFieldId.ANIMAUX_ACCEPTES:
      const prestationsAnimaux = (item as any).prestations || {};
      const animauxAcceptes = prestationsAnimaux.animauxAcceptes;
      if (!animauxAcceptes) return null;
      // Mapper les valeurs enum
      const animauxMap: Record<string, string> = {
        ACCEPTES: params.t("animalsAccepted") || "Animaux acceptés",
        NON_ACCEPTES: params.t("animalsNotAccepted") || "Animaux non acceptés",
        NON_DISPONIBLE: params.t("notAvailable") || "Non disponible",
      };
      const descriptifAnimauxAcceptes =
        prestationsAnimaux.descriptifAnimauxAcceptes;
      return {
        // Libellé affichable directement
        value: animauxMap[animauxAcceptes] || animauxAcceptes,
        // Valeur brute pour la logique métier (ACCEPTES / NON_ACCEPTES / ...)
        rawValue: animauxAcceptes,
        // Supplément éventuel (utilisé par getAnimalWording)
        animauxAcceptesSupplement: prestationsAnimaux.animauxAcceptesSupplement,
        // Descriptif textuel (traduit si possible)
        descriptifAnimauxAcceptes: descriptifAnimauxAcceptes
          ? params.getLabel(descriptifAnimauxAcceptes)
          : "",
      };

    case DisplayedFieldId.DESCRIPTIFS_THEMATISES: {
      const descriptifsThematises =
        (item.presentation as any)?.descriptifsThematises || [];
      if (descriptifsThematises.length === 0) return null;
      return {
        items: descriptifsThematises.map((dt: any) => {
          const theme = dt.theme;
          return {
            theme: {
              id: theme?.id ?? 0,
              enumKey: theme?.enumKey,
              label: params.getLabel(theme),
              ordre: theme?.ordre,
            },
            description: params.getLabel(dt.description),
          };
        }),
      };
    }

    case DisplayedFieldId.DESCRIPTIFS_PRIVES:
      // Les descriptifs privés sont dans donneesPrivees
      const donneesPrivees = (item as any).donneesPrivees || [];
      if (donneesPrivees.length === 0) return null;
      const dpConfig = params.fieldConfig?.data as
        | { nomsTechniques?: string[] }
        | undefined;
      if (!dpConfig?.nomsTechniques) return null;

      return {
        items: donneesPrivees
          .map((dp: any) => ({
            nomTechnique: dp.nomTechnique,
            title: dp.libelleFr,
            descriptif: params.getLabel(dp.descriptif),
            ordre: dp.ordre,
          }))
          .filter((dp: any) =>
            dpConfig.nomsTechniques?.includes(dp.nomTechnique),
          ),
      };

    case DisplayedFieldId.PUBLIC_NIVEAU: {
      // Le public niveau fait référence à prestations.typesClientele (TypeClientele = ElementReference)
      const typesClientele = (item as any).prestations?.typesClientele || [];
      if (typesClientele.length === 0) return null;
      return {
        items: typesClientele.map((tc: any) => ({
          id: tc?.id ?? 0,
          enumKey: tc?.enumKey,
          label: params.getLabel(tc),
        })),
      };
    }

    case DisplayedFieldId.OBJETS_LIES:
      const liens = (item as any).liens?.liensObjetsTouristiquesTypes || [];
      if (liens.length === 0) return null;
      return {
        items: liens.map((lien: any) => ({
          label: params.getLabel(lien) || lien.type,
          type: lien.type,
          objetTouristique: lien.objetTouristique,
        })),
      };

    case DisplayedFieldId.PRESTATAIRE_ACTIVITE:
      // Non implémenté (aucune section détail n'utilise ce champ).
      // Le prestataire d'activité fait référence à informationsActivite.prestataireActivites (objetTouristiqueLink)
      const prestataireActivites = (item as any).informationsActivite
        ?.prestataireActivites;
      if (!prestataireActivites) return null;
      // prestataireActivites est un objetTouristiqueLink avec type, id, nom, identifier, etc.
      return {
        value:
          params.getLabel(prestataireActivites.nom) ||
          prestataireActivites.nom?.libelleFr ||
          "",
        items: [
          {
            type: prestataireActivites.type,
            id: prestataireActivites.id,
            identifier: prestataireActivites.identifier,
            nom: params.getLabel(prestataireActivites.nom),
          },
        ],
      };

    case DisplayedFieldId.DISPONIBILITES_ELLOHA: {
      // Elloha : idProduct depuis item.metadonnees (noeudId "elloha"), idBookingEngine depuis la config du widget
      const ellohaFromItem = getEllohaWidgetFromItem(item);
      if (!ellohaFromItem?.idProduct) return null;
      const ellohaConfig = params.fieldConfig?.data as
        | { idBookingEngine?: string }
        | undefined;
      if (!ellohaConfig?.idBookingEngine) return null;
      return {
        value: params.t("availability") || "Disponibilités",
        ellohaWidget: {
          idProduct: ellohaFromItem.idProduct,
          idBookingEngine: ellohaConfig.idBookingEngine,
        },
      };
    }

    case DisplayedFieldId.DISPONIBILITES_OPENSYSTEM: {
      // OpenSystem : ui depuis item.metadonnees ; idPanier et idIntegration depuis la config du widget
      const openSystemFromItem = getOpenSystemWidgetFromItem(item);
      if (!openSystemFromItem?.ui) return null;
      const configData = params.fieldConfig?.data as
        | { idPanier?: string; idIntegration?: number }
        | undefined;
      if (!configData?.idPanier || configData.idIntegration == null)
        return null;
      return {
        value: params.t("availability") || "Disponibilités",
        openSystemWidget: {
          ui: openSystemFromItem.ui,
          uis: openSystemFromItem.uis,
          integration: {
            idPanier: configData.idPanier,
            id: configData.idIntegration,
          },
        },
      };
    }

    default:
      return null;
  }
}

/**
 * Données d'adresse formatées pour l'affichage (localisation, toujours requise à l'API).
 * Utilisé par la section Localisation + Contact.
 */
export interface LocationDisplayData {
  nomDuLieu: string;
  adresse1: string;
  adresse2: string;
  adresse3: string;
  codePostal: string;
  communeNom: string;
  cedex: string;
  bureauDistribution: string;
  paysLabel: string;
}

/**
 * Récupère les données d'adresse formatées pour l'affichage (item.localisation.adresse).
 * Le champ localisation est toujours récupéré via l'API (baseFields dans WidgetService).
 */
export function getLocationDisplayData(
  item: ObjetTouristique,
  params: Pick<DisplayedFieldUtilsParams, "getLabel">,
): LocationDisplayData | null {
  const adresse = item.localisation?.adresse;
  if (!adresse) return null;
  const commune = adresse.commune;
  const codePostal = adresse.codePostal ?? commune?.codePostal ?? "";
  const communeNom = commune?.nom ?? "";
  const paysLabel = commune?.pays ? params.getLabel(commune.pays) : "";
  const hasAny =
    adresse.nomDuLieu ||
    adresse.adresse1 ||
    adresse.adresse2 ||
    adresse.adresse3 ||
    codePostal ||
    communeNom ||
    adresse.cedex ||
    adresse.bureauDistribution ||
    paysLabel;
  if (!hasAny) return null;
  return {
    nomDuLieu: adresse.nomDuLieu ?? "",
    adresse1: adresse.adresse1 ?? "",
    adresse2: adresse.adresse2 ?? "",
    adresse3: adresse.adresse3 ?? "",
    codePostal,
    communeNom,
    cedex: adresse.cedex ?? "",
    bureauDistribution: adresse.bureauDistribution ?? "",
    paysLabel,
  };
}

/**
 * Récupère une valeur simple (string) pour un champ DisplayedFieldId
 * Utile pour les champs qui retournent uniquement une valeur textuelle
 */
export function getDisplayedFieldValue(
  item: ObjetTouristique,
  fieldId: string,
  params: DisplayedFieldUtilsParams,
): string | null {
  const data = getDisplayedFieldData(item, fieldId, params);
  if (!data) return null;
  return data.value || null;
}
